<template>
  <q-page>
    <div>
      <q-infinite-scroll :offset="250" @load="storeJobScroll.onLoad">
        <suggest-job
          v-if="recommendedJobs.length > 0"
          :stores="recommendedJobs || []"
          :title="title"
          :color="color"
          :icon="mdiMessageStarOutline"
          @card-click="handleCardClick"
        />
        <div
          v-else
          class="text-center text-accent q-pa-md row justify-center items-center"
        >
          No Job Available
        </div>
        <template #loading>
          <div class="row justify-center q-my-md">
            <q-spinner-dots color="primary" size="40px" />
          </div>
        </template>
      </q-infinite-scroll>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import SuggestJob from "@/components/card/SuggestJob.vue";
import { useHiringJob } from "@/composables/technician";
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";
import { useBase } from "@/composables/useBase";
import { JOB_ROUTE_NAME } from "@/libs/route-name";
import type { IStore } from "@/types/models";
import { mdiMessageStarOutline } from "@quasar/extras/mdi-v7";

const POST_JOBS_CACHE_KEY = "post-jobs-list";

const { t } = useI18n();
const route = useRoute();
const { getListHiringJob } = useHiringJob();
const { router, getQuery, getQueryNumber } = useBase();

const page = computed(() => getQueryNumber("page"));
const limit = computed(() => getQueryNumber("limit"));
const state = computed(() => getQuery("state") || "");
const city = computed(() => getQuery("city") || "");
const zipcode = computed(() => getQuery("zipcode") || "");

const checkQuery = computed(() => {
  return route.query.state || route.query.city || route.query.zipcode;
});

const storeJobScroll = useInfiniteScroll<IStore>(
  () =>
    getListHiringJob(
      page.value || 1,
      limit.value || 30,
      state.value,
      city.value,
      zipcode.value
    ),
  {
    initialPageSize: Number(limit.value),
    cacheKey: POST_JOBS_CACHE_KEY,
    useCache: true
  }
);

watch(
  () => route.query,
  async (newQuery, oldQuery) => {
    const filtersChanged =
      newQuery.state !== oldQuery?.state ||
      newQuery.city !== oldQuery?.city ||
      newQuery.zipcode !== oldQuery?.zipcode;

    if (filtersChanged) {
      await storeJobScroll.reset();
      await storeJobScroll.initialize();
    }
  },
  { immediate: false }
);

onMounted(async () => {
  await storeJobScroll.initialize();
});

const recommendedJobs = computed(() => {
  return storeJobScroll.allItems.value ?? [];
});

const title = computed(() => {
  if (checkQuery.value) {
    return t("page.job.search_result");
  }
  return t("page.job.recommended");
});

const color = computed(() => {
  if (route.query.state || route.query.city || route.query.zipcode) {
    return "primary";
  }
  return "dark";
});

const handleCardClick = (store: IStore) => {
  router.push({
    name: JOB_ROUTE_NAME.DETAIL,
    params: { id: store._id },
    query: {
      state: state.value,
      city: city.value,
      zipcode: zipcode.value
    }
  });
};
</script>

<style scoped></style>
